import rootStore from '@vue-storefront/core/store'
import { getSelectedBundleOptionValue } from 'theme/helpers/bundle';
import { SearchQuery } from 'storefront-query-builder'
import { quickSearchByQuery } from '@vue-storefront/core/lib/search'

/** Qty used when Magento inventory management is disabled (always orderable). */
const UNMANAGED_STOCK_QTY = 10000

const selectedBundleOptions = (product) => {
  return product.bundle_options.map((option) => {
    return getSelectedBundleOptionValue(product, option)
  })
}

export function hasManageStockFlag (product): boolean {
  const manageStock = product && product.stock && product.stock.manage_stock
  return manageStock === false || manageStock === 0 || manageStock === true || manageStock === 1
}

/**
 * Magento "Manage Stock" = No → always salable; skip MSI online qty gates.
 * Used for Muster simples. Bundles always have manage_stock=0 in Magento because
 * stock comes from selections — they must still respect the MSI "online" source.
 * Requires stock.manage_stock from vsbridge product index (or attachManageStock).
 */
export function isManageStockEnabled (product): boolean {
  if (product && product.type_id === 'bundle') {
    return true
  }

  const stock = product && product.stock
  if (!stock) {
    return true
  }

  return !(stock.manage_stock === false || stock.manage_stock === 0)
}

/**
 * Cart items omit full `stock` via optimizeShoppingCart — re-attach manage_stock from catalog when missing.
 */
export async function attachManageStock (products): Promise<void> {
  const list = (Array.isArray(products) ? products : [products]).filter(Boolean)
  const missing = list.filter(product => product.sku && !hasManageStockFlag(product))
  if (missing.length === 0) {
    return
  }

  const skus = [...new Set(missing.map(product => product.sku))]
  let query = new SearchQuery()
  query = query.applyFilter({ key: 'sku', value: { 'in': skus } })

  const { items = [] } = await quickSearchByQuery({
    query,
    size: skus.length,
    entityType: 'product',
    includeFields: ['sku', 'stock']
  })

  const manageStockBySku = {}
  for (const item of items) {
    if (!item || !item.sku || !item.stock) {
      continue
    }
    manageStockBySku[item.sku] = item.stock.manage_stock
  }

  for (const product of missing) {
    if (manageStockBySku[product.sku] === undefined) {
      continue
    }
    product.stock = {
      ...(product.stock || {}),
      manage_stock: manageStockBySku[product.sku]
    }
  }
}

export async function loadStock (product) {
  if (!product) {
    return
  }
  let skus = product.sku

  if (product.type_id === 'bundle') {
    skus = selectedBundleOptions(product).map((selectedOptionValue) => {
      return selectedOptionValue && selectedOptionValue.product ? selectedOptionValue.product.sku : null
    }).filter(Boolean)

    // Before bundle selections are hydrated, still load the primary link SKU(s)
    if (!skus.length) {
      const fallbackSku = prepareSku(product)
      skus = fallbackSku ? [fallbackSku] : []
      const linkSkus = (product.bundle_options || []).reduce((acc, option) => {
        const links = (option.product_links || []).map(link => link && link.sku).filter(Boolean)
        return acc.concat(links)
      }, [])
      skus = [...new Set(skus.concat(linkSkus))]
    }
  }
  return rootStore.dispatch('inventorySources/loadStock', skus)
}

export function prepareSku (product) {
  if (product) {
    if (product.type_id === 'bundle') {
      const option = product.bundle_options && product.bundle_options.find(o => true)
      const link = option && option.product_links && option.product_links.find(l => true)
      return link && link.sku
    }
    return product.sku
  }
}
export async function addStockItemsToCollection (items) {
  await rootStore.dispatch('inventorySources/loadStock', items.map(prepareSku))
  return items.map((product) => {
    return {
      ...product,
      inventoryStock: {
        ...resolveStock(product)
      }
    }
  })
}

/**
 * Re-resolve pickup/online qty from Vuex for already-loaded SKUs (no MSI refetch).
 * Use after the selected market changes.
 */
export function refreshCollectionInventoryStock (items = []) {
  return items.map((product) => {
    if (!product) return product
    return {
      ...product,
      inventoryStock: {
        ...resolveStock(product)
      }
    }
  })
}

/**
 * Pickup assignment = MSI source-item exists for that store (any status).
 * Magento status is In/Out of Stock (1/0), not "assigned".
 * Missing source-item → null (not orderable for C&C at that market).
 * Present with qty 0 / status 0 → 0 ("in 10 Werktagen" if online has stock).
 */
function resolvePickupQty (pickupStockItem) {
  if (!pickupStockItem) {
    return null
  }
  const quantity = pickupStockItem.quantity
  if (quantity === null || quantity === undefined) {
    return 0
  }
  return Math.floor(Number(quantity) || 0)
}

function resolveOnlineQty (onlineStockItem) {
  if (!onlineStockItem || onlineStockItem.quantity === null || onlineStockItem.quantity === undefined) {
    return null
  }
  return Math.floor(Number(onlineStockItem.quantity) || 0)
}

/**
 * Qty that can be ordered for the active channel.
 * Pickup = selected store + online source ("in 10 Werktagen" for the online part).
 * A missing online source counts as 0, so a pickup-only SKU cannot exceed the store qty.
 * unlimited = Magento manage stock off, or backorders.
 * assigned = false when Click & Collect has no source-item for the selected market.
 */
export function getOrderableQty (stock, isPickup) {
  if (!stock) {
    return { qty: 0, unlimited: false, assigned: !isPickup }
  }

  const unlimited = stock.manageStock === false || !!stock.backorders
  const onlineQty = Number(stock.qty) || 0

  if (isPickup) {
    const assigned = stock.qtyPickup !== null && stock.qtyPickup !== undefined
    const pickupQty = assigned ? (Number(stock.qtyPickup) || 0) : 0
    return {
      qty: assigned ? onlineQty + pickupQty : 0,
      unlimited: assigned && unlimited,
      assigned
    }
  }

  return { qty: onlineQty, unlimited, assigned: true }
}

export function resolveStock (product, resolveBundleOptionsStock = false) {
  if (resolveBundleOptionsStock && product.type_id === 'bundle') {
    return resolveBundleStock(product)
  }

  let sku = prepareSku(product)

  let stockItems = rootStore.getters['inventorySources/getCurrentStockItems']({ sku: sku })
  let onlineStockItem = stockItems.find(s => s.source_code === 'online')
  let pickupStockItem = stockItems.find(s => s.source_code !== 'online')

  const qtyPickup = resolvePickupQty(pickupStockItem)
  const manageStock = isManageStockEnabled(product)

  if (!manageStock) {
    return {
      qty: UNMANAGED_STOCK_QTY,
      qtyPickup,
      backorders: true,
      manageStock: false
    }
  }

  return {
    qty: resolveOnlineQty(onlineStockItem),
    qtyPickup,
    backorders: !!(onlineStockItem && onlineStockItem.extension_attributes && onlineStockItem.extension_attributes.backorders),
    manageStock: true
  }
}

export function resolveBundleStock (product) {
  let selectedOptionValues = selectedBundleOptions(product).map((selectedOptionValue) => {
    if (!selectedOptionValue || !selectedOptionValue.product) {
      return {
        qty: Infinity,
        qtyPickup: Infinity,
        backorders: false,
        manageStock: false
      }
    }
    return resolveStock(selectedOptionValue.product)
  })

  return selectedOptionValues.reduce((a: any, b: any) => {
    const qtyPickup =
      a.qtyPickup == null || b.qtyPickup == null
        ? null
        : Math.min(a.qtyPickup, b.qtyPickup)
    return {
      qty: Math.min(a.qty, b.qty),
      qtyPickup,
      backorders: a.backorders && b.backorders,
      manageStock: a.manageStock !== false || b.manageStock !== false
    }
  })
}
